import { createSlice } from '@reduxjs/toolkit';

import { SigFieldKeys } from 'enums/SigManagement.enum';
import { toDirectionIdentity } from 'modules/SigManagement/utils/directionIdentity';
import { SortOrder } from 'enums/common.enum';

const initialState = {
  currentPage: 1,
  pages: {},
  count: 0,
  filter: {},
  // Sig, created at and updated at are sortable; the counts and the status are not. The dates are
  // aggregates over the sig's associations, and the server sorts on the sig's own row using the
  // same expressions it selects them with, so the column and the order agree.
  sortBy: {
    sortKey: SigFieldKeys.SIG,
    sortOrder: SortOrder.ASCENDING,
  },
  // The one sig the detail dialog is showing. Held per sig id so reopening the same sig does not
  // flash the previous sig's rows while the new call is in flight.
  sigDetail: {
    sigId: undefined,
    directions: [],
    directionCount: 0,
    approvedDrugs: [],
    approvedDrugCount: 0,
    loading: false,
  },
  /**
   * Association changes staged but not committed.
   *
   * Nothing in here has reached the server. It outlives closing the dialog on purpose — a
   * pharmacist part-way through a set of changes who closes it by accident should find their work
   * where they left it, not gone. It does not outlive a page reload, which is the honest limit of
   * holding it in the store.
   *
   * `directionStatusEdits` and `catalogStatusEdits` map an existing id to the status it should end
   * up with. The `new*` lists are associations that do not exist yet. `sigText` is an edit to the
   * sig's own wording, staged like everything else so one commit applies the lot.
   */
  sigDraft: {
    sigId: undefined,
    sigText: undefined,
    directionStatusEdits: {},
    newDirections: [],
    catalogStatusEdits: {},
    newCatalogItems: [],
  },
};

const emptyDraftFor = (sigId) => ({ ...initialState.sigDraft, sigId });

/* eslint no-param-reassign: ["error", { "ignorePropertyModificationsFor": ["state"] }] */
const sigSlice = createSlice({
  name: 'sigs',
  initialState,
  reducers: {
    loadSigs(state, action) {
      const { page, items, count } = action.payload;

      state.count = count;
      state.pages[page] = items;
    },
    updateSigsCurrentPage(state, action) {
      const { page } = action.payload;

      state.currentPage = page;
    },
    updateSigsFilter(state, action) {
      const { filter } = action.payload;

      state.pages = {};
      state.count = 0;
      state.filter = filter;
    },
    updateSigsSortBy(state, action) {
      const { sortBy } = action.payload;

      state.pages = {};
      state.count = 0;
      state.sortBy = sortBy;
    },
    // Drops the cached pages and leaves the filter and the sort alone. A write changes what a page
    // holds, not what the user asked to see.
    invalidateSigPages(state) {
      state.pages = {};
      state.count = 0;
    },
    setSigListLoading(state, action) {
      state.loading = action.payload;
    },
    // The sig id is set as the fetch starts, so the dialog knows which sig its rows belong to and
    // can hold the previous ones back until the matching response lands.
    startSigDetailLoad(state, action) {
      const { sigId } = action.payload;

      state.sigDetail = { ...initialState.sigDetail, sigId, loading: true };
    },
    loadSigDetail(state, action) {
      const { sigId, directions, directionCount, approvedDrugs, approvedDrugCount } =
        action.payload;

      state.sigDetail = {
        sigId,
        directions,
        directionCount,
        approvedDrugs,
        approvedDrugCount,
        loading: false,
      };
    },
    // A failed load clears the rows rather than leaving the previous sig's on screen, and drops the
    // spinner so the dialog does not sit loading forever.
    failSigDetailLoad(state) {
      state.sigDetail = { ...initialState.sigDetail, sigId: state.sigDetail.sigId };
    },
    clearSigDetail(state) {
      state.sigDetail = initialState.sigDetail;
    },
    /**
     * Opening a different sig starts a fresh draft. Opening the SAME sig keeps the one already
     * there, which is what makes closing and reopening the dialog non-destructive.
     *
     * Only one draft is held, so opening a second sig — or Add New, whose draft is held under
     * NEW_SIG_DRAFT_ID — silently discards the first one's staged changes. That is a real gap and
     * it applies to every door into the dialog equally, not just Add New; holding a draft per sig,
     * or warning before discarding, is a change to this model rather than to one caller.
     */
    startSigDraft(state, action) {
      const { sigId } = action.payload;

      if (state.sigDraft.sigId !== sigId) {
        state.sigDraft = emptyDraftFor(sigId);
      }
    },
    // Undefined rather than the original text when the edit is dropped: "no edit staged" and "edited
    // back to what it was" then look the same to everything downstream, which is what they are.
    stageSigText(state, action) {
      state.sigDraft.sigText = action.payload.sigText;
    },
    unstageSigText(state) {
      state.sigDraft.sigText = undefined;
    },
    stageDirectionStatus(state, action) {
      const { directionId, isActive } = action.payload;

      state.sigDraft.directionStatusEdits[directionId] = isActive;
    },
    // Staging the status it already had is not a change, so the entry goes rather than sitting in
    // the summary claiming one.
    unstageDirectionStatus(state, action) {
      delete state.sigDraft.directionStatusEdits[action.payload.directionId];
    },
    // Deduped on the server's own rule for direction identity — see toDirectionIdentity. This
    // compared trimmed and lower-cased text, which let "Take  one" through against a staged
    // "Take one"; the server hashes both to the same direction and merges the second into nothing.
    //
    // The card refuses a duplicate before it gets here and says why, which is the part the reviewer
    // sees. This is the guard behind it, so no caller can stage one silently.
    stageNewDirection(state, action) {
      const { draftId, direction } = action.payload;
      const alreadyStaged = state.sigDraft.newDirections.some(
        (staged) => toDirectionIdentity(staged.direction) === toDirectionIdentity(direction)
      );

      if (!alreadyStaged) {
        state.sigDraft.newDirections.push({ draftId, direction });
      }
    },
    unstageNewDirection(state, action) {
      state.sigDraft.newDirections = state.sigDraft.newDirections.filter(
        ({ draftId }) => draftId !== action.payload.draftId
      );
    },
    stageCatalogStatus(state, action) {
      const { catalogId, isActive } = action.payload;

      state.sigDraft.catalogStatusEdits[catalogId] = isActive;
    },
    unstageCatalogStatus(state, action) {
      delete state.sigDraft.catalogStatusEdits[action.payload.catalogId];
    },
    // The whole catalog item, not just its id: the summary and the staged list have to show what
    // was picked before it has ever been fetched back from the server.
    stageNewCatalogItem(state, action) {
      const { catalogItem } = action.payload;

      if (!state.sigDraft.newCatalogItems.some((i) => i.catalogId === catalogItem.catalogId)) {
        state.sigDraft.newCatalogItems.push(catalogItem);
      }
    },
    unstageNewCatalogItem(state, action) {
      state.sigDraft.newCatalogItems = state.sigDraft.newCatalogItems.filter(
        ({ catalogId }) => catalogId !== action.payload.catalogId
      );
    },
    clearSigDraft(state) {
      state.sigDraft = initialState.sigDraft;
    },
    /**
     * Throws away everything staged for one sig, leaving the draft still pointing at it.
     *
     * Not `clearSigDraft`, which resets to the initial state and so leaves `sigId` undefined. The
     * dialog only shows and only submits staged changes whose draft belongs to the sig on screen,
     * so discarding with that reset left the dialog open on a draft it no longer recognised: the
     * next direction added went in and was never rendered, and Commit stayed disabled. Reopening
     * the dialog fixed it, which is not something the user would think to try.
     */
    discardSigDraft(state, action) {
      state.sigDraft = emptyDraftFor(action.payload.sigId);
    },
  },
});

export const sigActions = sigSlice.actions;
export default sigSlice.reducer;
